import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../api/client.jsx';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { usersFixture, userDetailFixture, userTenantsFixture } from './fixture';

/**
 * Server-side LIKE-search + status filter.
 * Spec: GET /v1/admin/users?q=&status=
 */
export function useUsers({ search = '', status = '' } = {}) {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();

  return useQuery({
    queryKey: ['users', { search, status }, fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return usersFixture({ search, status });
      }
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (status) params.set('status', status);
      const qs = params.toString();
      return fetch(`/v1/admin/users${qs ? `?${qs}` : ''}`);
    },
    staleTime: 30_000,
  });
}

/** GET /v1/admin/users/:id */
export function useUserDetail(userId) {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();

  return useQuery({
    queryKey: ['user', userId, fixturesMode ? 'fixture' : 'live'],
    enabled: !!userId,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay(150);
        const data = userDetailFixture(userId);
        if (!data) throw new Error('user_not_found_in_tenant');
        return data;
      }
      return fetch(`/v1/admin/users/${userId}`);
    },
  });
}

/** GET /v1/admin/users/:id/tenants — Memberships für JIT-Operator */
export function useUserTenants(userId) {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();

  return useQuery({
    queryKey: ['userTenants', userId, fixturesMode ? 'fixture' : 'live'],
    enabled: !!userId,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay(100);
        return userTenantsFixture(userId);
      }
      return fetch(`/v1/admin/users/${userId}/tenants`);
    },
  });
}
